import React from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import ProtectedRoute from "@/routes/ProtectedRoute";
import PaidRoute from "@/routes/PaidRoute";
import DashboardLayout from "@/components/layouts/DashboardLayout";
import { AuthProvider, useAuth } from "@/state/AuthContext";
import { isPlatformAdmin, isPlatformViewer } from "@/utils/access";
import { ErrorBoundary } from "@/components/system/ErrorBoundary";
import { RouteLogger } from "@/components/routing/RouteLogger";

// Auth pages
import LoginPage from "@/pages/auth/LoginPage";
import { SignupPage } from "@/pages/auth/SignupPage";
import { InvitePage } from "@/pages/auth/InvitePage";
import { VerifyEmailPage } from "@/pages/auth/VerifyEmailPage";
import { InviteAcceptPage } from "@/pages/auth/InviteAcceptPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";

// System pages
import { NotFound } from "@/pages/system/NotFound";
import { Forbidden } from "@/pages/system/Forbidden";

// Views
import SelectWorkspacePage from "@/pages/workspaces/SelectWorkspacePage";
import GuestHomePage from "@/pages/home/GuestHomePage";
import RequireWorkspace from "@/routes/RequireWorkspace";
import HomeRoute from "@/routes/HomeRoute";
import LegacyWorkspaceProjectRedirect from "@/routes/LegacyWorkspaceProjectRedirect";
import { useWorkspacePersistHydrated } from "@/hooks/useWorkspacePersistHydrated";
import { DashboardsIndex } from "@/views/dashboards/Index";
import { DashboardBuilder } from "@/views/dashboards/Builder";
import DashboardView from "@/views/dashboards/View";
import WorkspacesIndexPage from "@/views/workspaces/WorkspacesIndexPage";
import WorkspaceView from "@/views/workspaces/WorkspaceView";
import WorkspaceMembersPage from "@/features/workspaces/pages/WorkspaceMembersPage";
import WorkspaceSettingsPage from "@/features/workspaces/settings/WorkspaceSettingsPage";
import WorkspaceHomePage from "@/pages/workspaces/WorkspaceHomePage";
import TemplateRouteSwitch from "@/pages/templates/TemplateRouteSwitch";
import DocsPage from "@/pages/docs/DocsPage";
import FormsPage from "@/pages/forms/FormsPage";
import { ProjectPlanView } from "@/views/work-management/ProjectPlanView";
import { ProjectPageLayout } from "@/features/projects/layout";
import { ProjectOverviewTab, ProjectGovernanceTab, ProjectTasksTab, ProjectBoardTab, ProjectTableTab, ProjectGanttTab, ProjectKpisTab, ProjectDocumentWorkflowTab, ProjectBudgetTab } from "@/features/projects/tabs";
import { NotEnabledInProject } from "@/features/projects/components/NotEnabledInProject";
import ProjectsPage from "@/pages/projects/ProjectsPage";
import SettingsPage from "@/pages/settings/SettingsPage";
import SecuritySettingsPage from "@/pages/settings/SecuritySettingsPage";
import InboxPage from "@/pages/InboxPage";
import ArtifactPage from "@/pages/ArtifactPage/ArtifactPage";
import ResourcesPage from "@/pages/ResourcesPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import OnboardingPage from "@/pages/onboarding/OnboardingPage";
import CreateFirstWorkspacePage from "@/pages/onboarding/CreateFirstWorkspacePage";
import GateReviewPage from "@/pages/GateReviewPage";
import {
  AccessPage,
  ApplyPage,
  ContactPage,
  DesignPartnerLayout,
  FaqPage,
  HowItWorksPage,
  PrivacyPage,
  ProductPage,
  RoadmapPage,
  SecurityPage,
  TermsPage,
} from "@/features/design-partner";
import { LandingV2Page } from "@/features/landing-v2";
/** Calendar MVP PR 1 — lazy only; do not add to @/features/projects/tabs barrel. */
const ProjectCalendarTab = React.lazy(() => import("@/features/projects/tabs/ProjectCalendarTab"));
import { ResourceHeatmapPage } from "@/pages/resources/ResourceHeatmapPage";
import { ResourceTimelinePage } from "@/pages/resources/ResourceTimelinePage";
import JoinWorkspacePage from "@/views/workspaces/JoinWorkspacePage";
import WorkspaceSlugRedirect from "@/views/workspaces/WorkspaceSlugRedirect";
import WorkspaceHomeBySlug from "@/views/workspaces/WorkspaceHomeBySlug";
// PHASE 6 MODULE 3-4: Program and Portfolio pages
import ProgramsListPage from "@/pages/programs/ProgramsListPage";
import ProgramDetailPage from "@/pages/programs/ProgramDetailPage";
import PortfoliosListPage from "@/pages/portfolios/PortfoliosListPage";
import PortfolioDetailPage from "@/pages/portfolios/PortfolioDetailPage";
import FeaturesRoute from "@/routes/FeaturesRoute";
// PHASE 7 MODULE 7.2: My Work
import MyWorkPage from "@/pages/my-work/MyWorkPage";
import FavoritesPage from "@/pages/favorites/FavoritesPage";
// Phase 2E: Capacity Engine
import CapacityPage from "@/features/capacity/CapacityPage";
// Phase 4A: Organization Command Center
import OrgDashboardPage from "@/features/org-dashboard/OrgDashboardPage";
import ScenarioPage from "@/features/scenarios/ScenarioPage";
import { isScenariosUiEnabled } from "@/features/scenarios/scenariosUi";
import AdministrationLayout from "@/features/administration/layout/AdministrationLayout";
import { FeatureRoute } from "@/features/feature-switches/FeatureRoute";
import AdministrationOverviewPage from "@/features/administration/pages/AdministrationOverviewPage";
import AdministrationGovernancePage from "@/features/administration/pages/AdministrationGovernancePage";
import AdministrationTemplatesPage from "@/features/administration/pages/AdministrationTemplatesPage";
import AdministrationPeoplePage from "@/features/administration/pages/AdministrationPeoplePage";
import AdministrationSecurityPage from "@/features/administration/pages/AdministrationSecurityPage";
import AdministrationOrganizationPage from "@/features/administration/pages/AdministrationOrganizationPage";
import AdministrationTeamsPage from "@/features/administration/pages/AdministrationTeamsPage";
import AdministrationNotificationsPage from "@/features/administration/pages/AdministrationNotificationsPage";
import AdministrationAuditTrailPage from "@/features/administration/pages/AdministrationAuditTrailPage";
import AdministrationGeneralPage from "@/features/administration/pages/AdministrationGeneralPage";
import AdministrationTrashPage from "@/features/administration/pages/AdministrationTrashPage";
import AdminProfilePage from "@/features/administration/pages/AdminProfilePage";
import AdminPreferencesPage from "@/features/administration/pages/AdminPreferencesPage";
import AppAuthenticatedChrome from "@/components/shell/AppAuthenticatedChrome";
import { UserThemeSync } from "@/components/system/UserThemeSync";
import { Skeleton } from "@/components/ui/feedback/Skeleton";
// RisksPage — org-level list (Item 3 surface recon)
import ProjectRisksPage from "@/features/projects/risks/ProjectRisksPage";
import ProjectChangeRequestsTab from "@/features/projects/tabs/ProjectChangeRequestsTab";
import { useWorkspaceStore } from "@/state/workspace.store";
import { clearUserSelectLock } from "@/lib/dom/clearUserSelectLock";

/** "/" — authenticated users go to Inbox (inbox-first); guests see the V2 landing */
function RootRoute() {
  const { user, isLoading } = useAuth();
  if (isLoading) return null; // wait for auth check
  if (user) return <Navigate to="/inbox" replace />;
  return <LandingV2Page />;
}

/** Primary "Work" route: workspace-aware entrypoint */
function WorkRoute() {
  const hydrated = useWorkspacePersistHydrated();
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  if (!hydrated) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="work-route-hydrating">
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }
  if (activeWorkspaceId) return <Navigate to="/projects" replace />;
  return <Navigate to="/workspaces" replace />;
}

/** Documents entry for current IA; routes into Work context */
function DocumentsRoute() {
  const hydrated = useWorkspacePersistHydrated();
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  if (!hydrated) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="documents-route-hydrating">
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }
  if (activeWorkspaceId) return <Navigate to={`/workspaces/${activeWorkspaceId}/home`} replace />;
  return <Navigate to="/workspaces" replace />;
}

/**
 * Phase 2A: Inline admin-only route guard for routes outside AdminLayout
 * (e.g. /billing). Redirects non-admins to /403.
 */
function RequireAdminInline({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const isAdmin = isPlatformAdmin(user);
  if (!isAdmin) {
    return (
      <Navigate
        to={`/403?from=${encodeURIComponent(loc.pathname)}&reason=need_org_admin`}
        replace
      />
    );
  }
  return children;
}

/**
 * SETTINGS-SHELL-1 — non-Admins hitting /administration/* land on Settings › Profile.
 * Server admin API refusal is unchanged (AdminGuard).
 */
function RedirectNonAdminToSettings({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!isPlatformAdmin(user)) return <Navigate to="/settings/profile" replace />;
  return children;
}

/**
 * Phase 2A: Inline paid-user guard — blocks VIEWER (guest) from accessing
 * routes that require Admin or Member role (e.g. /templates, /workspaces/:id/members).
 */
function RequirePaidInline({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (isPlatformViewer(user)) return <Navigate to="/inbox" replace />;
  return children;
}

function isPlatformAdminUser(user: ReturnType<typeof useAuth>["user"]): boolean {
  return isPlatformAdmin(user);
}

/** /administration index: admins → overview; others → Settings › Profile. */
function AdministrationIndexRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (isPlatformAdminUser(user)) return <Navigate to="/administration/overview" replace />;
  return <Navigate to="/settings/profile" replace />;
}

function DndUserSelectCleanup() {
  const location = useLocation();

  React.useEffect(() => {
    clearUserSelectLock();
  }, []);

  React.useEffect(() => {
    const clear = () => clearUserSelectLock();
    clear();

    window.addEventListener("dragend", clear);
    window.addEventListener("mouseup", clear);
    window.addEventListener("pointerup", clear);
    window.addEventListener("touchend", clear);
    window.addEventListener("visibilitychange", clear);
    window.addEventListener("blur", clear);
    window.addEventListener("focus", clear);
    window.addEventListener("keyup", clear);

    return () => {
      window.removeEventListener("dragend", clear);
      window.removeEventListener("mouseup", clear);
      window.removeEventListener("pointerup", clear);
      window.removeEventListener("touchend", clear);
      window.removeEventListener("visibilitychange", clear);
      window.removeEventListener("blur", clear);
      window.removeEventListener("focus", clear);
      window.removeEventListener("keyup", clear);
      clear();
    };
  }, [location.pathname]);

  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <Toaster position="top-right" />
        <UserThemeSync />
        <RouteLogger />
        <DndUserSelectCleanup />
        <Routes>
          {/* Marketing landing — V2 */}
          <Route path="/" element={<RootRoute />} />
          {/* Public routes — design-partner marketing site */}
          <Route element={<DesignPartnerLayout />}>
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/product" element={<ProductPage />} />
            <Route path="/roadmap" element={<RoadmapPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/apply" element={<ApplyPage />} />
            <Route path="/access" element={<AccessPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Route>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/invites/accept" element={<InviteAcceptPage />} />
          <Route path="/invite" element={<InvitePage />} />
          <Route path="/join/workspace" element={<JoinWorkspacePage />} />

          {/* Protected routes with shell */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppAuthenticatedChrome />}>
            {/* Onboarding route (no layout) */}
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/setup/workspace" element={<CreateFirstWorkspacePage />} />

            {/* Main app routes with DashboardLayout */}
            <Route element={
              <ErrorBoundary>
                <DashboardLayout />
              </ErrorBoundary>
            }>
              {/* ── Org-level routes (no workspace required) ── */}
              {/* Inbox-first: all roles land here after login */}
              <Route path="/inbox" element={<InboxPage />} />
              <Route path="/home" element={<HomeRoute />} />
              {/* Gate review uses the same shell. It is not workspace-gated:
                  the server resolves the workspace from the submission row. */}
              <Route path="/gates/:submissionId/review" element={<GateReviewPage />} />
              <Route path="/work" element={<WorkRoute />} />
              <Route path="/documents" element={<DocumentsRoute />} />
              <Route path="/select-workspace" element={<SelectWorkspacePage />} />
              <Route path="/guest/home" element={<GuestHomePage />} />
              <Route path="/workspaces" element={<WorkspacesIndexPage />} />
              {/* Legacy tabbed SettingsPage — not the SETTINGS-SHELL-1 entry. */}
              <Route path="/settings/legacy" element={<SettingsPage />} />
              {/* W1-4 / ADMIN-NAV-1: no billing page in the MVP (TR-2026-10-15-32). Old /billing bookmarks go to Administration. */}
              <Route path="/billing" element={<Navigate to="/administration" replace />} />
              <Route path="/org-dashboard" element={
                <FeatureRoute feature="org-command-center" redirectTo="/home">
                  <RequireAdminInline><OrgDashboardPage /></RequireAdminInline>
                </FeatureRoute>
              } />
              {/* WHAT-IF-1: Admin-only. SCENARIOS-PROD-OFF-1: route omitted unless
                  VITE_SCENARIOS_ENABLED=true at build time. Missing flag → /scenarios
                  falls through to * → /404. */}
              {isScenariosUiEnabled() && (
              <Route path="/scenarios" element={<RequireAdminInline><ScenarioPage /></RequireAdminInline>} />
              )}
              {/* Admin Home lives at /home (HomeRoute). /admin-home only forwards old bookmarks. */}
              <Route path="/admin-home" element={<Navigate to="/home" replace />} />

              {/* Pass 3: Dashboards directory — all authenticated roles may open (Viewer read-only; taxonomy §3.9). */}
              <Route path="/dashboards" element={<DashboardsIndex />} />

              {/* My Work — org-level queue; paid Admin/Member only; no active workspace required */}
              <Route element={<PaidRoute />}>
                <Route path="/my-work" element={<MyWorkPage />} />
              </Route>

              {/* Favorites — org-scoped list; all authenticated roles with access to targets */}
              <Route path="/favorites" element={<FavoritesPage />} />

              {/* ── Workspace-scoped routes (in-page empty state if none selected) ── */}
              <Route element={<RequireWorkspace />}>
                {/* B2: slug-canonical workspace entry — auth + layout; slug bootstrap exempt from activeWorkspaceId */}
                <Route path="/w/:slug" element={<WorkspaceSlugRedirect />} />
                <Route path="/w/:slug/home" element={<WorkspaceHomeBySlug />} />
                <Route path="/reports" element={<Navigate to="/analytics" replace />} />
                <Route path="/dashboards/:id" element={<DashboardView />} />
                <Route path="/dashboards/:id/edit" element={<DashboardBuilder />} />
                <Route path="/projects" element={<ProjectsPage />} />
                {/* ADR-004 — legacy blank-create deep links → Template Center (no orphan modals) */}
                <Route path="/projects/new" element={<Navigate to="/templates" replace />} />
                <Route path="/project/new" element={<Navigate to="/templates" replace />} />
                <Route path="/create" element={<Navigate to="/templates" replace />} />
                <Route path="/create-project" element={<Navigate to="/templates" replace />} />
                <Route
                  path="/projects/:projectId/artifacts/:artifactId"
                  element={<ArtifactPage />}
                />
                {/* Project detail pages with tabbed layout */}
                {/* Phase 2 (Template Center HR3): Only 4 tabs are visible. Hidden tabs render NotEnabledInProject. */}
                <Route path="/projects/:projectId" element={<ProjectPageLayout />}>
                  <Route index element={<ProjectOverviewTab />} />
                  <Route path="tasks" element={<ProjectTasksTab />} />
                  <Route path="board" element={<ProjectBoardTab />} />
                  <Route path="gantt" element={<ProjectGanttTab />} />
                  <Route
                    path="calendar"
                    element={
                      <FeatureRoute feature="projects.calendar" redirectTo="..">
                      <React.Suspense
                        fallback={
                          <div className="p-4 space-y-4" data-testid="calendar-tab-suspense">
                            <Skeleton className="h-8 w-full max-w-md" />
                            <Skeleton className="min-h-[28rem] w-full" />
                          </div>
                        }
                      >
                        <ProjectCalendarTab />
                      </React.Suspense>
                      </FeatureRoute>
                    }
                  />
                  {/* Plan tab mounts the real Plan surface (gates + WBS). Deep link /work/projects/:id/plan preserved below. */}
                  <Route path="plan" element={<ProjectPlanView />} />
                  <Route path="table" element={<ProjectTableTab />} />
                  <Route path="governance" element={<ProjectGovernanceTab />} />
                  <Route path="risks" element={<ProjectRisksPage />} />
                  <Route
                    path="resources"
                    element={
                      <FeatureRoute feature="projects.resources" redirectTo="..">
                        <NotEnabledInProject featureName="Resources" description="The Resource & Capacity Management Engine is on the platform roadmap and is not active in the MVP shell." />
                      </FeatureRoute>
                    }
                  />
                  <Route
                    path="change-requests"
                    element={
                      <FeatureRoute feature="projects.change-requests" redirectTo="..">
                        <ProjectChangeRequestsTab />
                      </FeatureRoute>
                    }
                  />
                  <Route path="documents" element={<ProjectDocumentWorkflowTab />} />
                  {/* TC-F2b: Tools dropdown Docs / Doc Workflow kept as aliases → Documents tab */}
                  <Route path="tools/docs" element={<Navigate to="../documents" replace />} />
                  <Route path="tools/workflow" element={<Navigate to="../documents" replace />} />
                  {/* MOUNT-BUDGET-EVM-PANEL-1: real EVM + cost summary (not roadmap placeholder). */}
                  <Route path="budget" element={<ProjectBudgetTab />} />
                  <Route
                    path="kpis"
                    element={
                      <FeatureRoute feature="projects.kpis" redirectTo="..">
                        <ProjectKpisTab />
                      </FeatureRoute>
                    }
                  />
                </Route>
                {/* Legacy route redirect for backwards compatibility */}
                <Route path="/work/projects/:projectId/plan" element={<ProjectPlanView />} />
                <Route path="/workspaces/:workspaceId/home" element={<WorkspaceHomePage />} />
                {/* Dead historical deep link — redirect to canonical project route */}
                <Route
                  path="/workspaces/:workspaceId/projects/:projectId"
                  element={<LegacyWorkspaceProjectRedirect />}
                />
                <Route path="/workspaces/:id" element={<WorkspaceView />} />
                {/* Phase 2A: Members page blocked for VIEWER at route level */}
                <Route path="/workspaces/:id/members" element={<RequirePaidInline><WorkspaceMembersPage /></RequirePaidInline>} />
                <Route path="/workspaces/:id/settings" element={<WorkspaceSettingsPage />} />
                <Route path="/workspaces/:id/heatmap" element={<FeatureRoute feature="resources" redirectTo="/home"><ResourceHeatmapPage /></FeatureRoute>} />
                {/* Operational / deep-link heatmap, behind the resources switch */}
                <Route path="/heatmap" element={<FeatureRoute feature="resources" redirectTo="/home"><ResourceHeatmapPage /></FeatureRoute>} />
                {/* PHASE 6 MODULE 3-4: Program and Portfolio routes - Gated by feature flag */}
                <Route element={<FeaturesRoute feature="programsPortfolios" />}>
                  <Route path="/workspaces/:workspaceId/programs" element={<ProgramsListPage />} />
                  <Route path="/workspaces/:workspaceId/programs/:programId" element={<ProgramDetailPage />} />
                  <Route path="/workspaces/:workspaceId/portfolios" element={<PortfoliosListPage />} />
                  <Route path="/workspaces/:workspaceId/portfolios/:portfolioId" element={<PortfolioDetailPage />} />
                </Route>
                {/* Templates: Viewer may view catalog read-only (`template.view`); writes enforced server-side. */}
                <Route path="/templates" element={<TemplateRouteSwitch />} />
                <Route path="/docs/:docId" element={<DocsPage />} />
                <Route path="/forms/:formId/edit" element={<FormsPage />} />
                <Route path="/resources" element={<FeatureRoute feature="resources" redirectTo="/home"><ResourcesPage /></FeatureRoute>} />
                <Route path="/resources/:id/timeline" element={<ResourceTimelinePage />} />
                <Route path="/analytics" element={<AnalyticsPage />} />
                {/* Phase 2E: Capacity Engine — operational / deep-link, behind the resources switch */}
                <Route path="/capacity" element={<FeatureRoute feature="resources" redirectTo="/home"><CapacityPage /></FeatureRoute>} />
              </Route>
              <Route path="/403" element={<Forbidden />} />
              <Route path="/404" element={<NotFound />} />
            </Route>

            {/*
             * SETTINGS-SHELL-1 — one Settings & Administration shell.
             * MY ACCOUNT at /settings/*; admin surfaces at /administration/*
             * (non-Admins redirected to /settings/profile; server auth unchanged).
             */}
            <Route element={<AdministrationLayout />}>
              <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
              <Route path="/settings/profile" element={<AdminProfilePage />} />
              <Route path="/settings/preferences" element={<AdminPreferencesPage />} />
              <Route path="/settings/notifications" element={<AdministrationNotificationsPage />} />
              <Route
                path="/settings/appearance"
                element={<Navigate to="/settings/preferences#appearance" replace />}
              />
              <Route path="/settings/security" element={<SecuritySettingsPage />} />

              <Route path="/administration" element={<AdministrationIndexRoute />} />
              <Route
                path="/administration/overview"
                element={
                  <RedirectNonAdminToSettings>
                    <AdministrationOverviewPage />
                  </RedirectNonAdminToSettings>
                }
              />
              <Route path="/administration/profile" element={<Navigate to="/settings/profile" replace />} />
              <Route
                path="/administration/preferences"
                element={<Navigate to="/settings/preferences#appearance" replace />}
              />
              <Route path="/administration/notifications" element={<Navigate to="/settings/notifications" replace />} />
              <Route
                path="/administration/general"
                element={
                  <RedirectNonAdminToSettings>
                    <AdministrationGeneralPage />
                  </RedirectNonAdminToSettings>
                }
              />
              <Route
                path="/administration/governance"
                element={
                  <RedirectNonAdminToSettings>
                    <AdministrationGovernancePage />
                  </RedirectNonAdminToSettings>
                }
              />
              <Route
                path="/administration/workspaces"
                element={
                  <RedirectNonAdminToSettings>
                    <Navigate to="/administration?workspaces=1" replace />
                  </RedirectNonAdminToSettings>
                }
              />
              <Route
                path="/administration/templates"
                element={
                  <RedirectNonAdminToSettings>
                    <AdministrationTemplatesPage />
                  </RedirectNonAdminToSettings>
                }
              />
              <Route
                path="/administration/people"
                element={
                  <RedirectNonAdminToSettings>
                    <AdministrationPeoplePage />
                  </RedirectNonAdminToSettings>
                }
              />
              <Route
                path="/administration/security"
                element={
                  <RedirectNonAdminToSettings>
                    <AdministrationSecurityPage />
                  </RedirectNonAdminToSettings>
                }
              />
              <Route
                path="/administration/organization"
                element={
                  <RedirectNonAdminToSettings>
                    <AdministrationOrganizationPage />
                  </RedirectNonAdminToSettings>
                }
              />
              <Route
                path="/administration/teams"
                element={
                  <FeatureRoute feature="teams" redirectTo="/administration/general">
                    <RedirectNonAdminToSettings>
                      <Navigate to="/administration/people?tab=teams" replace />
                    </RedirectNonAdminToSettings>
                  </FeatureRoute>
                }
              />
              <Route
                path="/administration/audit-trail"
                element={
                  <RedirectNonAdminToSettings>
                    <AdministrationAuditTrailPage />
                  </RedirectNonAdminToSettings>
                }
              />
              {/*
               * ADMIN-NAV-1 (TR-2026-10-15-32, FR-2026-10-15-18) — no route for
               * Billing, Import / Export, Integrations, Capacity or Workload heatmap
               * here. A typed URL falls through to * → /404. Resources is listed in
               * the panel as "Coming soon" only.
               */}
              <Route
                path="/administration/trash"
                element={
                  <RedirectNonAdminToSettings>
                    <AdministrationTrashPage />
                  </RedirectNonAdminToSettings>
                }
              />
              <Route
                path="/administration/users"
                element={
                  <RedirectNonAdminToSettings>
                    <Navigate to="/administration/people" replace />
                  </RedirectNonAdminToSettings>
                }
              />
              <Route
                path="/administration/audit-log"
                element={
                  <RedirectNonAdminToSettings>
                    <Navigate to="/administration/audit-trail" replace />
                  </RedirectNonAdminToSettings>
                }
              />
              <Route
                path="/administration/settings"
                element={
                  <RedirectNonAdminToSettings>
                    <Navigate to="/administration/security" replace />
                  </RedirectNonAdminToSettings>
                }
              />
            </Route>
            <Route path="/admin" element={<Navigate to="/administration" replace />} />
            <Route path="/admin/*" element={<Navigate to="/administration" replace />} />

            </Route>
          </Route>

          {/* Default redirects */}
          <Route path="/dashboard" element={<Navigate to="/dashboards" replace />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}
